import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { differenceInDays } from 'date-fns';
import { ConfigBaseFilter, ListLayout, ListLayoutSingleComponent, SimpleConfigBaseFilter } from 'gung-list';
import {
  AuthService,
  MetadataService,
  Order,
  OrderCardListComponent,
  OrderIdFilter,
  OrderListConfigService,
  OrderService,
  SelectedCustomerService
} from 'gung-standard';
import { first, Observable, of, switchMap } from 'rxjs';
import { map, mergeMap } from 'rxjs';
import { CultOrderCardListComponent } from '../components/cult-order-card-list/cult-order-card-list.component';
import { DateUtilService } from 'gung-common';

export class OrderDateFilter extends SimpleConfigBaseFilter<Order> {
  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'ORDER_DATE';
  }

  getOptionIds(item: Order): string[] {
    const dayDifferense = differenceInDays(new Date(), new Date(item.extra.oh.orddatum));

    if (dayDifferense <= 1) {
      return ['TODAY'];
    }
    if (dayDifferense <= 7) {
      return ['THIS_WEEK'];
    }
    if (dayDifferense <= 30) {
      return ['THIS_MONTH'];
    }
    if (dayDifferense <= 90) {
      return ['LAST_THREE_MONTHS'];
    }
    return ['OVER_THREE_MONTHS'];
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

class OrderExpectedDeliveryFilter extends SimpleConfigBaseFilter<Order> {
  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'EXPECTED_DELIVERY_DATE';
  }

  getOptionIds(item: Order): string[] {
    const dayDifferense = differenceInDays(new Date(), new Date(item.extra.oh.ordberlevdat));

    if (dayDifferense <= 1) {
      return ['TODAY'];
    }
    if (dayDifferense <= 7) {
      return ['THIS_WEEK'];
    }
    if (dayDifferense <= 30) {
      return ['THIS_MONTH'];
    }
    if (dayDifferense <= 90) {
      return ['LAST_THREE_MONTHS'];
    }
    return ['OVER_THREE_MONTHS'];
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

class OrderStatusFilter extends SimpleConfigBaseFilter<Order> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'ORDER_STATUS';
  }
  getOptionIds(item: Order): string[] {
    const value = this.metadataService.getMetadataValue('x7', 'ordstatbeskr', item.extra.oh.ordstat);
    return [value];
  }
  getOptionName(key: string): string {
    return key;
  }
}

class SalesPersonFilter extends SimpleConfigBaseFilter<Order> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'SALESPERSON';
  }

  getOptionIds(item: Order): string[] {
    return [item.extra.oh.saljare];
  }
  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('salj', 'saljarenamn', key);
  }
}

@Injectable({
  providedIn: 'root'
})
export class CultOrderListConfigService extends OrderListConfigService {
  protected translateService = inject(TranslateService);
  protected metadataService = inject(MetadataService);
  protected authService = inject(AuthService);

  getLayouts(): ListLayout<Order>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => CultOrderCardListComponent,
        getListLayoutComponent: () => ListLayoutSingleComponent,
        getName: () => 'Order list'
      }
    ];
  }

  getItems(): Observable<Order[]> {
    return this.authService.getRoles().pipe(
      first(),
      switchMap(roles => {
        const isOnlyReturnsUser = roles.indexOf('SHOW_ONLY_RETURNS') !== -1;

        if (this.customerId == null) {
          return this.selectedCustomerService.getSelectedCustomer().pipe(
            mergeMap(customer => {
              if (customer != null) {
                return this.orderService.getOrders(customer.id, false, this.maxSize).pipe(
                  map(order =>
                    order.filter(data => {
                      const base = data?.extra?.oh?.ordstat !== 90;
                      if (isOnlyReturnsUser) {
                        return base && data.extra?.oh?.salestype === '11' && this.checkYear(data.extra?.oh?.orddatum);
                      }
                      return base;
                    })
                  )
                );
              } else {
                return of([]);
              }
            })
          );
        } else {
          return this.orderService.getOrders(this.customerId, false, this.maxSize).pipe(
            map(order =>
              order.filter(data => {
                const base = data?.extra?.oh?.ordstat !== 90;
                if (isOnlyReturnsUser) {
                  return base && data.extra?.oh?.salestype === '11' && this.checkYear(data.extra?.oh?.orddatum);
                }
                return base;
              })
            )
          );
        }
      })
    );
  }

  getFilters(): ConfigBaseFilter<Order>[] {
    const filters = super.getFilters();
    filters.push(new OrderDateFilter(this.translateService));
    filters.push(new OrderExpectedDeliveryFilter(this.translateService));
    filters.push(new OrderStatusFilter(this.metadataService));
    filters.push(new SalesPersonFilter(this.metadataService));
    return filters;
  }

  checkYear(date) {
    if (!date) {
      return false;
    }
    const newDate = new Date(date);
    if (newDate && newDate.getFullYear() >= 2023) {
      return true;
    }
  }
}
